import React from "react";
import { useLocation, BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import Studio from "@/pages/Studio";
import Analyst from "@/pages/Analyst";
import Markets from "@/pages/Markets";
import Reports from "@/pages/Reports";
import MetaIntegration from "@/pages/MetaIntegration";
import Settings from "@/pages/Settings";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import DataDeletion from "@/pages/DataDeletion";
import CookieConsent from "@/components/CookieConsent";

const LEGAL_PATHS = ["/privacy", "/terms", "/cookies", "/data-deletion"];

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0D0E]">
      <div className="h-10 w-10 rounded-full border-2 border-lime-400 border-t-transparent animate-spin" />
    </div>
  );
}

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/analyst" element={<Analyst />} />
        <Route path="/markets" element={<Markets />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/meta-integration" element={<MetaIntegration />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (LEGAL_PATHS.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
      </Routes>
    );
  }
  if (loading) return <LoadingScreen />;
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return <ProtectedRoutes />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <CookieConsent />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
